import { Box, VStack } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { useEffect, useState } from 'react';

import { useAuth } from 'auth/AuthProvider';
import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import Modal from 'components/Modal';
import Text from 'components/Text';
import { ERoute } from 'types/frontend';

const DISMISSED_KEY_SUFFIX = '-legacy-promo-dismissed';

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const LegacyPromoModal = () => {
  const { userId } = useAuth();
  const { isFree, isLegacy } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!userId || !isFree || !isLegacy) {
      return;
    }

    const dismissed = localStorage.getItem(`${userId}${DISMISSED_KEY_SUFFIX}`);

    if (!dismissed) {
      // Small delay so it doesn't fight with page load
      const timer = setTimeout(() => setIsOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [userId, isFree, isLegacy]);

  const handleClose = () => {
    if (userId) {
      localStorage.setItem(`${userId}${DISMISSED_KEY_SUFFIX}`, 'true');
    }
    setIsOpen(false);
  };

  if (!isFree || !isLegacy) {
    return null;
  }

  return (
    <Modal size="md" isOpen={isOpen} onClose={handleClose} header={null} footer={null}>
      <VStack spacing={5} py={4} px={1}>
        {/* Top badge */}
        <Box
          px={3}
          py={1}
          borderRadius="full"
          bg="whiteAlpha.100"
          border="1px solid"
          borderColor="whiteAlpha.200"
          animation={`${fadeIn} 0.5s ease-out`}
        >
          <Text fontSize="micro" fontWeight="semibold" opacity="secondary" letterSpacing="0.05em">
            OG MEMBER EXCLUSIVE
          </Text>
        </Box>

        {/* Main headline */}
        <VStack spacing={2} animation={`${fadeIn} 0.5s ease-out 0.1s both`}>
          <Text fontSize="xxLarge" fontWeight="bold" textAlign="center" lineHeight="1.3">
            BigDevSoon got a
            <br />
            massive upgrade
          </Text>
          <Text
            fontWeight="regular"
            opacity="secondary"
            textAlign="center"
            fontSize="small"
            maxW="320px"
          >
            AI vibe coding, multi-framework editor, file storage, and more — built from the ground up for you.
          </Text>
        </VStack>

        {/* Promo code card */}
        <Box
          w="100%"
          p={4}
          borderRadius={12}
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
          animation={`${fadeIn} 0.5s ease-out 0.2s both`}
        >
          <VStack spacing={3}>
            <Text fontSize="small" opacity="secondary" fontWeight="regular">
              Your exclusive code
            </Text>

            <Box
              px={5}
              py={2.5}
              borderRadius={8}
              bg="whiteAlpha.100"
              border="1px dashed"
              borderColor="whiteAlpha.300"
              position="relative"
              overflow="hidden"
            >
              <Text
                fontSize="xLarge"
                fontWeight="bold"
                letterSpacing="0.12em"
                bgGradient="linear(to-r, orange.200, yellow.200, orange.200)"
                bgClip="text"
                bgSize="200% auto"
                animation={`${shimmer} 3s linear infinite`}
              >
                VIBECODE50
              </Text>
            </Box>

            <Text fontSize="small" fontWeight="regular" opacity="tertiary" textAlign="center">
              50% off any plan — limited to the first 100 users
            </Text>
          </VStack>
        </Box>

        {/* CTA */}
        <Box w="100%" animation={`${fadeIn} 0.5s ease-out 0.3s both`}>
          <Button
            scheme="gradient"
            fullWidth
            size="lg"
            to={ERoute.Plans}
            onClick={handleClose}
          >
            View plans
          </Button>
        </Box>

        {/* Dismiss text */}
        <Text
          fontSize="micro"
          opacity="tertiary"
          fontWeight="regular"
          cursor="pointer"
          _hover={{ opacity: 0.6 }}
          transition="opacity 0.2s"
          onClick={handleClose}
          animation={`${fadeIn} 0.5s ease-out 0.4s both`}
        >
          Maybe later
        </Text>
      </VStack>
    </Modal>
  );
};

export default LegacyPromoModal;
